import { UseTranslationResponse } from 'react-i18next';
import { IColumnProps } from 'devextreme-react/data-grid';
import clsx from 'clsx';
import formStyles from '../../../../components/form/form.module.scss';
import { formatDate } from '../../../../helpers/abas-helper.ts';
import styles from '../OutstandingReceivables.module.scss';
import { Tip } from '../../../../components/uikit/Tip.tsx';
import CreditedImage from '../../../../assets/icons/success-round.svg';
import RepaidImage from '../../../../assets/icons/status_accepted.svg';
import DebtImage from '../../../../assets/icons/status_warning.svg';
import OverdueDebtImage from '../../../../assets/icons/error.svg';
import React from 'react';
import SortArrow from 'assets/icons/sort-arrow.svg?react';
import SortArrowHover from 'assets/icons/sort-arrow-hover.svg?react';
import i18n from 'i18next';
import {
  Credited,
  Debt,
  OverdueDebt,
  Repaid,
} from '../OutstandingReceivables.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';

const TitleTooltipBody = () => {
  return (
    <div data-test-id={'status-tooltip-body'} className={styles.tooltipText}>
      {i18n.t('Page.OutstandingReceivables.tooltip_text')}
    </div>
  );
};

export const getColumnsConfig = (
  i18n: UseTranslationResponse<'translation', 'Page.OutstandingReceivables'>,
  isDesc: boolean,
  changeSorting: (name: string) => void,
  sortBy?: string | null,
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'invoiceNumber',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('invoice'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 170,
  },
  {
    dataField: 'dated',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('dated'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 192,
    cellRender: (cell) => formatDate(cell.value),
  },
  {
    dataField: 'dueDate',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('due_date'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 192,
    cellRender: (cell) => formatDate(cell.value),
  },
  {
    dataField: 'open',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('open'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 192,
    cellRender: (cell) => {
      try {
        return (
          <div className={clsx(styles.tooltipContainer, styles.columnGap4)}>
            <div>{Number(cell.value).toFixed(2)}</div>
            <div className={styles.greyText}>{cell.data?.currency}</div>
          </div>
        );
      } catch (e) {
        return null;
      }
    },
  },
  {
    dataField: 'invoiceAmount',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('invoice_amount'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 170,
    cellRender: (cell) => {
      try {
        return (
          <div className={clsx(styles.tooltipContainer, styles.columnGap4)}>
            <div>{cell.value.toFixed(2)}</div>
            <div className={styles.greyText}>{cell.data?.currency}</div>
          </div>
        );
      } catch (e) {
        return null;
      }
    },
  },
  {
    dataField: 'dunningLevel',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('dunning_level'),
    cssClass: clsx(
      formStyles.textCellPadding,
      styles.textAlignLeft,
      formStyles.tableHeaderPointer,
    ),
    width: 144,
  },
  {
    dataField: 'invoiceStatus',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('status'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 120,
    headerCellRender: (info) => {
      const isSorted = info.column.dataField === sortBy;

      return (
        <div className={styles.tooltipContainer}>
          <div className={clsx(isSorted && styles.black)}>
            {info.column.caption}
          </div>
          <div
            className={styles.marginRight5}
            onClick={(e) => e.stopPropagation()}
          >
            <Tip
              testId={'status-tooltip'}
              tooltipBody={<TitleTooltipBody />}
              defaultIconClassname={styles.tooltipIcon}
              className={styles.tooltip}
              tooltipClassName={styles.additionalMargins}
            />
          </div>
          {isSorted ? (
            isDesc ? (
              <SortArrow />
            ) : (
              <SortArrow className={styles.rotate180} />
            )
          ) : (
            <div className={clsx(styles.sortIconContainer, styles.displayNone)}>
              <SortArrowHover className={clsx(styles.rotate180)} />
            </div>
          )}
        </div>
      );
    },
    cellRender: (row) => {
      const value = row.value;

      if (value === Credited) {
        return (
          <Tip
            className={styles.unsetBackgroundColor}
            tooltipText={i18n.t('credited')}
            customImage={CreditedImage}
            testId={'credited-tooltip'}
          />
        );
      }

      if (value === Repaid) {
        return (
          <Tip
            className={styles.unsetBackgroundColor}
            tooltipText={i18n.t('repaid')}
            customImage={RepaidImage}
            testId={'repaid-tooltip'}
          />
        );
      }

      if (value === Debt) {
        return (
          <Tip
            className={styles.unsetBackgroundColor}
            tooltipText={i18n.t('debt')}
            customImage={DebtImage}
            testId={'debt-tooltip'}
          />
        );
      }

      return (
        <Tip
          className={styles.unsetBackgroundColor}
          tooltipText={i18n.t('overdue_debt')}
          customImage={OverdueDebtImage}
          testId={'overdue-debt-tooltip'}
        />
      );
    },
  },
];

export const Total = (props: { generalStatus: string; totalSum: number }) => {
  const i18n = useScopedTranslation('Page.OutstandingReceivables');

  const { totalSum, generalStatus } = props;

  const tooltipInfo = {
    image: RepaidImage,
    text: i18n.t('repaid'),
  };

  if (generalStatus === OverdueDebt) {
    tooltipInfo.image = OverdueDebtImage;
    tooltipInfo.text = i18n.t('overdue_debt');
  } else if (generalStatus === Debt) {
    tooltipInfo.image = DebtImage;
    tooltipInfo.text = i18n.t('debt');
  } else if (generalStatus === Credited) {
    tooltipInfo.image = CreditedImage;
    tooltipInfo.text = i18n.t('credited');
  }

  return (
    <div
      data-test-id={'total'}
      className={clsx(
        styles.totalRow,
        totalSum > 0 && styles.backgroundRed,
        totalSum < 0 && styles.backgroundGreen,
      )}
    >
      <div className={clsx(styles.flex1, styles.bold)}>{i18n.t('total')}</div>
      <div
        className={clsx(
          styles.tooltipContainer,
          styles.flex1,
          styles.spaceBetween,
        )}
      >
        <div
          className={clsx(
            styles.tooltipContainer,
            styles.columnGap4,
            styles.marginLeft15,
          )}
        >
          <div className={styles.bold}>{totalSum.toFixed(2)}</div>
        </div>
        <Tip
          className={clsx(styles.unsetBackgroundColor)}
          tooltipText={tooltipInfo.text}
          customImage={tooltipInfo.image}
        />
      </div>
    </div>
  );
};
